import { BaseInputField } from "@components/commerce/baseForms/BaseInputField";
import DeliveryInstructionsField from "@components/commerce/baseForms/DeliveryInstructionsField";
import { RegionField } from "@components/commerce/baseForms/RegionField";
import { Address } from "@lib/constants/contentful";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Button } from "@ecco/ui/button";
import { useFormikContext } from "formik";
import type { FormikState } from "formik";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { styles } from "./DeliveryStepFormStyles";
import { getFormsConfig } from "./FormsConfig";
import { AddressLookup } from "@components/commerce/baseForms/AddressLookup/AddressLookup";

import {
    ADDITIONAL_ADDRESS_TEST_ID,
    ADDRESS_TEST_ID,
    CITY_TEST_ID,
    DELIVERY_DATE,
    DELIVERY_INSTRUCTIONS,
    FIRST_NAME_TEST_ID,
    LANDMARK_TEST_ID,
    LAST_NAME_TEST_ID,
    PHONETIC_FIRST_NAME_TEST_ID,
    PHONETIC_LAST_NAME_TEST_ID,
    REGION_TEST_ID,
    DISTRICT_TEST_ID,
    ZIP_CODE_TEST_ID,
    DELIVERY_TIME,
    IS_COMPANY_CHECKBOX_TEST_ID,
    COMPANY_TEST_ID,
    SALUTATION_TEST_ID,
} from "@e2e/helpers/checkout/components/shipping-details/constants";
import { Market, type CountryCode } from "@config/site/types";
import { getAddressLookupFieldKey } from "@components/commerce/baseForms/helpers/helpers";
import DeliveryDatePicker from "@components/commerce/baseForms/DeliveryDatePicker";
import {
    getDeliveryDates,
    isDateSelectorEnabled,
} from "@features/checkout/utils/deliveryDatesAndTime";
import TimeslotSelector from "@components/commerce/baseForms/TimeslotSelector";
import { fieldStateHelper } from "@lib/utils/form/fieldStateHelper";
import { hasHouseNumber } from "@lib/utils/address";
import { useFormFieldsMicrocopy } from "./FormFields.microcopy";
import type { CheckoutFormValues } from "./types";
import { Checkbox } from "@ecco/ui/checkbox";
import { Flex } from "@ecco/ui/flex";
import { FormControl } from "@ecco/ui/form-control";
import { FormLabel } from "@ecco/ui/form-label";
import { ShippingMethodTypes } from "@features/checkout/types";
import SalutationField from "@components/commerce/baseForms/Salutation/SalutationField";
import { getPostalCodeData } from "@components/commerce/baseForms/utils/getPostalCodeData";
import { useQueryClient } from "@tanstack/react-query";
import { getPostalCodeValidationResult } from "./utils/postal-code-validation/validatePostalCode";
import { useShippingMethods } from "@features/checkout/hooks/useShippingMethods";
import { useApi } from "@lib/helpers/useApi";
import { useDebouncedValidation } from "./utils/useDebouncedValidation";
import { getPostalCodeFormatter } from "./utils/formatting/getPostalCodeFormatter";
import { prefetchInvalidPostalCodesForCountry } from "@components/commerce/baseForms/utils/getInvalidPostalCodesForCountry";
import type { AddressLookupFieldValues } from "@components/commerce/baseForms/AddressLookup/useAddressLookup";
import { isSuccessResult } from "./utils/postal-code-validation/utils";
import { isParcelLockerAddress } from "./utils/parcel-locker/isParcelLockerAddress";
import { useIsLoyaltyMember } from "@hooks/useIsLoyaltyMember";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";

interface AddressFormFieldsProps {
    prefix: string;
    country: CountryCode;
    isBillingForm: boolean;
    locale: string;
    deliveryMethodId?: string;
    cartId?: string;
}

type FormikStateValues = CheckoutFormValues["deliveryAddress"];

const AddressFormFields = ({
    prefix,
    country,
    isBillingForm,
    locale,
    deliveryMethodId,
    cartId,
}: AddressFormFieldsProps) => {
    const {
        billingFields,
        shippingFields,
        postalCodeLookup,
        disabledFields,
        showAdditionalAddressByDefault,
        additionalAddressRequired,
        enableCompanyDelivery,
        additionalAddressCollapsible,
    } = getFormsConfig(country);

    const {
        status,
        errors: checkoutFormErrors,
        values: checkoutFormValues,
        touched: checkoutFormTouched,
        setFieldValue,
        handleBlur,
        setStatus,
        setFieldTouched,
        setFieldError,
        validateField,
    } = useFormikContext<CheckoutFormValues>();
    const { headers } = useFormFieldsMicrocopy();

    const { data: shippingMethods } = useShippingMethods();

    const selectedCarrierId = useMemo(() => {
        return shippingMethods?.find((method) => method.key === deliveryMethodId)?.carrierCode;
    }, [deliveryMethodId, shippingMethods]);

    const queryClient = useQueryClient();

    useEffect(() => {
        if (values?.postalCode) {
            handlePostalAsyncActions(values?.postalCode);
        }
    }, []);

    // Prefetch invalid postal codes for the selected country.
    useEffect(() => {
        prefetchInvalidPostalCodesForCountry(country, queryClient);
    }, [country, queryClient]);

    // We assume deliveryAddress for types since billingAddress is a subset of deliveryAddress and this saves us from uneccessary type checks.
    const values: FormikState<FormikStateValues>["values"] = checkoutFormValues[prefix];
    const errors: FormikState<FormikStateValues>["errors"] = checkoutFormErrors[prefix];
    const touched: FormikState<FormikStateValues>["touched"] = checkoutFormTouched[prefix];

    const isStateFieldEnabled = useFeatureFlag("isStateFieldEnabled");
    const isDistrictFieldEnabled = useFeatureFlag("isDistrictFieldEnabled");

    // State (region) and district fields are only shown for locales where the
    // corresponding feature flag is enabled, so they aren't sent to D365 otherwise.
    const formFields = (isBillingForm ? billingFields : shippingFields).filter((field) => {
        if (field === Address.form.region && !isStateFieldEnabled) return false;
        if (field === Address.form.district && !isDistrictFieldEnabled) return false;
        return true;
    });
    const [showAdditionalAddressField, setShowAdditionalAddressField] = useState(
        // Collapsible markets (CA) still start expanded when a value is
        // prefilled, so a saved additional address is never hidden.
        showAdditionalAddressByDefault ||
            (Boolean(additionalAddressCollapsible) && Boolean(values?.additionalAddressInfo))
    );

    const { data: deliveryDates = [] } = useApi.useAsync(
        ["get-delivery-dates", values?.region, country, locale, cartId],
        async () => {
            const listOfDeliveryDates = await getDeliveryDates(
                cartId,
                country,
                values.region,
                locale
            );
            return listOfDeliveryDates?.dates || [];
        },
        {
            enabled: !!values.region && !!cartId,
            retry: 1,
            staleTime: 15 * 60 * 1000, // 15 minutes
        }
    );

    useEffect(() => {
        if (values?.postalCode) {
            validateField(`${prefix}.postalCode`);
        }
    }, [selectedCarrierId]);

    const streetName = values.streetName?.trim();
    const showTooltip = Boolean(touched.streetName && streetName && !hasHouseNumber(streetName));
    // A nudge towards the Packstation delivery option, never a block, as those orders still deliver.
    const showParcelLockerHint =
        Boolean(headers.parcelLockerHint) && isParcelLockerAddress(streetName, country);

    const getAndSetPostalCodeData = async (postalCode: string) => {
        if (!postalCodeLookup || isBillingForm || !postalCode) return;
        setStatus({ postalCodeError: undefined });
        try {
            const data = await getPostalCodeData({
                country,
                postalCode,
                queryClient,
            });

            if (data) {
                setFieldValue(`${prefix}.city`, data.city, false);
                setFieldValue(`${prefix}.region`, data.stateCodeCc);
            }
        } catch {
            setStatus({ postalCodeError: headers.errors.zipCodeValid });
        }
    };

    const onPostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = event.target.value;
        const fieldName = event.target.name;

        const formatter = getPostalCodeFormatter(country);
        if (formatter) {
            inputValue = formatter(inputValue);
        }
        setFieldValue(fieldName, inputValue, false);
        // Mark as touched but defer Yup validation to our debounced/blur flows
        setFieldTouched(fieldName, true, false);
        // Clear any stale Yup error so the required error is not shown while typing
        setFieldError(fieldName, undefined);

        // Skip lookup and validation for billing form
        if (isBillingForm) {
            return;
        }
        // Clear any existing postal code error when user starts typing
        if (status?.postalCodeError) {
            setStatus({ postalCodeError: "" });
        }

        // Trigger debounced validation
        // SE: validate once full format reached (3 digits + space + 2 => length 6)
        // Others: keep existing threshold (>=4)
        const shouldValidateNow =
            country.toLocaleLowerCase() === Market.SE || country.toLocaleLowerCase() === Market.IN
                ? inputValue.length >= 6
                : inputValue.length >= 4;
        if (shouldValidateNow) {
            handlePostalAsyncActions(inputValue);
        }
    };

    // Handle action for postal code blur
    const handlePostalAsyncActions = async (postalCodeValue?: string) => {
        // Always validate on blur for any country if a value exists
        if (postalCodeValue && !isBillingForm) {
            const validationError = await validatePostalCodeCallback(postalCodeValue);
            if (validationError) {
                setStatus({ postalCodeError: validationError });
            } else {
                setStatus({ postalCodeError: "" });
                await getAndSetPostalCodeData(postalCodeValue);
            }
        }
    };

    const onPostalCodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // We need to call Formik's handleBlur here to make sure that the field is marked as touched
        handleBlur(e);
        const postalCodeValue = e.target.value.trim();
        handlePostalAsyncActions(postalCodeValue);
    };

    const isFieldReadOnly = (field: string) => {
        return !isBillingForm && disabledFields.includes(field);
    };

    const { getFieldStateProps, getIconProps } = fieldStateHelper({
        errors,
        touched,
    });

    const { isLoyaltyMember } = useIsLoyaltyMember();

    // Club members can't check out as a business, so the toggle is hidden and
    // the address form stays personal.
    const hideBusinessToggle = isLoyaltyMember;

    const customerType =
        enableCompanyDelivery && !hideBusinessToggle && values.isCompany ? "COMPANY" : "PRIVATE";

    // Keep the submitted payload in sync with the hidden toggle: clear any
    // prefilled company data so the billing address stays personal.
    useEffect(() => {
        if (!hideBusinessToggle) return;
        if (values.isCompany) setFieldValue(`${prefix}.isCompany`, false);
        if (values.company) setFieldValue(`${prefix}.company`, "");
    }, [hideBusinessToggle, values.isCompany, values.company, prefix, setFieldValue]);

    const validatePostalCode = async (value?: string): undefined | Promise<string | undefined> => {
        if (!value) {
            return;
        }
        const result = await getPostalCodeValidationResult({
            postalCode: value,
            country,
            isBillingForm,
            headers,
            carrierCode: selectedCarrierId,
        });
        if (isSuccessResult(result)) return;

        return result.message;
    };

    const onAddressLookupSelect = (fields: AddressLookupFieldValues) => {
        handlePostalAsyncActions(fields.postalCode);
    };

    const validatePostalCodeCallback = useDebouncedValidation({
        validationFunction: validatePostalCode,
        debounceTime: 300,
        dependency: selectedCarrierId,
        debounceConfig: { leading: false, trailing: true },
    });

    const getPostalCodeState = () => {
        const hasAsyncError = !!status?.postalCodeError && !isBillingForm;
        const isSuccess = !errors?.postalCode && touched?.postalCode && !hasAsyncError;
        const isInvalid = (errors?.postalCode && touched?.postalCode) || hasAsyncError;
        const error = !isBillingForm
            ? (errors?.postalCode ?? status?.postalCodeError)
            : errors?.postalCode;
        return { isSuccess, isInvalid, error };
    };
    const postalCodeState = getPostalCodeState();

    return (
        <>
            {formFields.map((singleField) => {
                switch (singleField) {
                    // Keys in Contentful
                    case customerType === "PRIVATE" && Address.form.firstName:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("firstName")}
                                    {...getIconProps("firstName")}
                                    isReadOnly={isFieldReadOnly("firstName")}
                                    id={`${prefix}.firstName`}
                                    testId={`${prefix}-${FIRST_NAME_TEST_ID}`}
                                    header={headers.firstName}
                                />
                            </Box>
                        );
                    case customerType === "PRIVATE" && Address.form.lastName:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("lastName")}
                                    {...getIconProps("lastName")}
                                    isReadOnly={isFieldReadOnly("lastName")}
                                    id={`${prefix}.lastName`}
                                    testId={`${prefix}-${LAST_NAME_TEST_ID}`}
                                    header={headers.lastName}
                                />
                            </Box>
                        );
                    case enableCompanyDelivery && !hideBusinessToggle && Address.form.isCompany:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <FormControl>
                                    <Flex gap="3" alignItems="center">
                                        <Checkbox
                                            colorScheme="black"
                                            data-testid={IS_COMPANY_CHECKBOX_TEST_ID}
                                            id={`${prefix}.isCompany`}
                                            name={`${prefix}.isCompany`}
                                            isChecked={values.isCompany}
                                            onChange={() => {
                                                setFieldValue(
                                                    `${prefix}.isCompany`,
                                                    !values.isCompany
                                                );
                                            }}
                                        />
                                        <FormLabel
                                            htmlFor={`${prefix}.isCompany`}
                                            fontWeight="normal"
                                            _hover={{ cursor: "pointer" }}
                                            marginBottom={"0 "}
                                        >
                                            {headers.isCompany}
                                        </FormLabel>
                                    </Flex>
                                </FormControl>
                            </Box>
                        );

                    case customerType === "COMPANY" && Address.form.company:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("company")}
                                    {...getIconProps("company")}
                                    isReadOnly={isFieldReadOnly("company")}
                                    id={`${prefix}.company`}
                                    testId={`${prefix}-${COMPANY_TEST_ID}`}
                                    header={headers.company}
                                />
                            </Box>
                        );
                    case customerType === "PRIVATE" && Address.form.phoneticFirstName:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("phoneticFirstName")}
                                    {...getIconProps("phoneticFirstName")}
                                    isReadOnly={isFieldReadOnly("phoneticFirstName")}
                                    id={`${prefix}.phoneticFirstName`}
                                    testId={`${prefix}-${PHONETIC_FIRST_NAME_TEST_ID}`}
                                    header={headers.phoneticFirstName}
                                />
                            </Box>
                        );
                    case customerType === "PRIVATE" && Address.form.phoneticLastName:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("phoneticLastName")}
                                    {...getIconProps("phoneticLastName")}
                                    isReadOnly={isFieldReadOnly("phoneticLastName")}
                                    id={`${prefix}.phoneticLastName`}
                                    testId={`${prefix}-${PHONETIC_LAST_NAME_TEST_ID}`}
                                    header={headers.phoneticLastName}
                                />
                            </Box>
                        );
                    case Address.form.addressLookup: {
                        const fieldKey = getAddressLookupFieldKey(country);

                        return (
                            <AddressLookup
                                {...getFieldStateProps(fieldKey)}
                                // Address lookup field should not show success as lookups are not always vali
                                isSuccess={false}
                                isReadOnly={isFieldReadOnly(fieldKey)}
                                key={singleField}
                                header={headers?.[fieldKey]}
                                country={country}
                                locale={locale}
                                prefix={prefix}
                                id={`${prefix}.${fieldKey}`}
                                // Wins over the house number warning, as a Packstation's numbers pass that check.
                                tooltipLabel={
                                    (showParcelLockerHint
                                        ? headers.parcelLockerHint
                                        : headers.tooltipLabelStreetNumber) ?? ""
                                }
                                noAddressesFound={headers.noAddressesFound}
                                showTooltip={
                                    fieldKey === "streetName"
                                        ? showParcelLockerHint || showTooltip
                                        : false
                                }
                                helpText={headers.streetHelpText || undefined}
                                helpTextColor="gray.500"
                                onAddressSelect={onAddressLookupSelect}
                            />
                        );
                    }
                    case Address.form.streetName:
                        return (
                            <Fragment key={singleField}>
                                <Box sx={styles.fieldWrapper}>
                                    <Flex gap={2} flexDirection="column">
                                        <BaseInputField
                                            {...getFieldStateProps("streetName")}
                                            {...getIconProps("streetName")}
                                            isReadOnly={isFieldReadOnly("streetName")}
                                            id={`${prefix}.streetName`}
                                            testId={`${prefix}-${ADDRESS_TEST_ID}`}
                                            header={headers.streetName}
                                            helpText={
                                                headers.streetHelpText ||
                                                (showTooltip
                                                    ? headers.tooltipLabelStreetNumber
                                                    : undefined)
                                            }
                                            helpTextColor={
                                                headers.streetHelpText ? "gray.500" : "black"
                                            }
                                            autocomplete="address-line1"
                                        />
                                    </Flex>
                                </Box>

                                {showAdditionalAddressField ? (
                                    <Box sx={styles.fieldWrapper}>
                                        <BaseInputField
                                            {...getFieldStateProps("additionalAddressInfo")}
                                            {...getIconProps("additionalAddressInfo")}
                                            isReadOnly={isFieldReadOnly("additionalAddressInfo")}
                                            required={additionalAddressRequired}
                                            id={`${prefix}.additionalAddressInfo`}
                                            testId={`${prefix}-${ADDITIONAL_ADDRESS_TEST_ID}`}
                                            header={
                                                country === "JP"
                                                    ? headers.requiredAdditionalStreetName
                                                    : headers.addressLine2
                                            }
                                            helpText={
                                                headers.additionalAddressHelpText || undefined
                                            }
                                            helpTextColor="gray.500"
                                            autocomplete="address-line2"
                                        />
                                    </Box>
                                ) : (
                                    <Text
                                        sx={styles.additionalAddress}
                                        onClick={() => setShowAdditionalAddressField(true)}
                                    >
                                        {headers.additionalAddressInfo}
                                    </Text>
                                )}
                            </Fragment>
                        );

                    case formFields.indexOf(Address.form.streetName) === -1 &&
                        Address.form.addAddressLine: {
                        // CA: a disclosure button toggles the optional Address Line 2 input.
                        // Collapsed it shows the trigger copy; expanded it becomes the field
                        // label (the input has no visible label and is named via
                        // aria-labelledby) and clicking it hides the input again. When
                        // expanded, button + input share a single field wrapper so their
                        // spacing matches the other fields. Other markets keep the
                        // always-visible labelled input.
                        if (additionalAddressCollapsible) {
                            const additionalAddressInputId = `${prefix}.additionalAddressInfo`;
                            const additionalAddressLabelId = `${additionalAddressInputId}-label`;
                            if (!showAdditionalAddressField) {
                                return (
                                    <Button
                                        key={singleField}
                                        variant="link"
                                        sx={styles.additionalAddressTrigger}
                                        aria-expanded={false}
                                        onClick={() => setShowAdditionalAddressField(true)}
                                    >
                                        {/* Fall back to the Address Line 2 label until the
                                            trigger microcopy exists in Contentful. */}
                                        {headers.additionalAddressTrigger || headers.addressLine2}
                                    </Button>
                                );
                            }
                            return (
                                <Box key={singleField} sx={styles.additionalAddressExpandedWrapper}>
                                    <Button
                                        id={additionalAddressLabelId}
                                        variant="link"
                                        sx={styles.additionalAddressExpandedLabel}
                                        aria-expanded={true}
                                        aria-controls={additionalAddressInputId}
                                        onClick={() => setShowAdditionalAddressField(false)}
                                    >
                                        {headers.additionalAddressExpanded || headers.addressLine2}
                                    </Button>
                                    <BaseInputField
                                        {...getFieldStateProps("additionalAddressInfo")}
                                        {...getIconProps("additionalAddressInfo")}
                                        isReadOnly={isFieldReadOnly("additionalAddressInfo")}
                                        id={additionalAddressInputId}
                                        required={additionalAddressRequired}
                                        testId={`${prefix}-${ADDITIONAL_ADDRESS_TEST_ID}`}
                                        header=""
                                        ariaLabelledBy={additionalAddressLabelId}
                                        helpText={headers.additionalAddressHelpText || undefined}
                                        helpTextColor="gray.500"
                                        autocomplete="address-line2"
                                    />
                                </Box>
                            );
                        }

                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("additionalAddressInfo")}
                                    {...getIconProps("additionalAddressInfo")}
                                    isReadOnly={isFieldReadOnly("additionalAddressInfo")}
                                    id={`${prefix}.additionalAddressInfo`}
                                    required={additionalAddressRequired}
                                    testId={`${prefix}-${ADDITIONAL_ADDRESS_TEST_ID}`}
                                    // Fall back to the "add address line" copy until addressLine2
                                    // exists in Contentful for this locale, so the field is never unlabelled.
                                    header={headers.addressLine2 || headers.additionalAddressInfo}
                                    helpText={headers.additionalAddressHelpText || undefined}
                                    helpTextColor="gray.500"
                                    autocomplete="address-line2"
                                />
                            </Box>
                        );
                    }

                    case Address.form.zipCode:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getIconProps("postalCode")}
                                    testId={`${prefix}-${ZIP_CODE_TEST_ID}`}
                                    id={`${prefix}.postalCode`}
                                    isSuccess={postalCodeState.isSuccess}
                                    isInvalid={postalCodeState.isInvalid}
                                    error={postalCodeState.error}
                                    isReadOnly={isFieldReadOnly(singleField)}
                                    header={headers.postalCode}
                                    onBlur={onPostalCodeBlur}
                                    onChange={onPostalCodeChange}
                                />
                            </Box>
                        );
                    case Address.form.city:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("city")}
                                    {...getIconProps("city")}
                                    isReadOnly={isFieldReadOnly("city")}
                                    id={`${prefix}.city`}
                                    testId={`${prefix}-${CITY_TEST_ID}`}
                                    header={headers.city}
                                />
                            </Box>
                        );
                    case Address.form.district:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getFieldStateProps("district")}
                                    {...getIconProps("district")}
                                    isReadOnly={isFieldReadOnly("district")}
                                    id={`${prefix}.district`}
                                    testId={`${prefix}-${DISTRICT_TEST_ID}`}
                                    header={headers.district}
                                    required
                                />
                            </Box>
                        );
                    case Address.form.region:
                        return (
                            <RegionField
                                testId={`${prefix}-${REGION_TEST_ID}`}
                                key={singleField}
                                prefix={`${prefix}.`}
                                error={errors?.region}
                                value={values.region}
                                header={headers.region}
                                title={headers.placeholderDropdown}
                                isSuccess={!errors?.region && touched?.region}
                                isInvalid={!!errors?.region && touched?.region}
                                setFieldValue={setFieldValue}
                                country={country}
                                locale={locale}
                                style={styles.fieldWrapper}
                                stateDropdownStyle={styles.dropdownStyle}
                                isReadOnly={isFieldReadOnly(singleField)}
                            />
                        );
                    case Address.form.landmark:
                        return (
                            <Box key={singleField} sx={styles.fieldWrapper}>
                                <BaseInputField
                                    {...getIconProps("landmark")}
                                    {...getFieldStateProps("landmark")}
                                    isReadOnly={isFieldReadOnly("landmark")}
                                    id={`${prefix}.landmark`}
                                    required={false}
                                    testId={`${prefix}-${LANDMARK_TEST_ID}`}
                                    header={headers.landmark}
                                />
                            </Box>
                        );
                    case Address.form.deliveryInstructions:
                        return (
                            <DeliveryInstructionsField
                                key={singleField}
                                testId={`${prefix}-${DELIVERY_INSTRUCTIONS}`}
                                id={`${prefix}.deliveryInstructions`}
                                isInvalid={
                                    !!errors?.deliveryInstructions && touched?.deliveryInstructions
                                }
                                error={errors?.deliveryInstructions}
                                header={headers.deliveryInstructions}
                                maxLength={100}
                                currentLength={values.deliveryInstructions?.length || 0}
                                isReadOnly={isFieldReadOnly(singleField)}
                            />
                        );
                    case Address.form.deliveryDatePicker: {
                        if (isDateSelectorEnabled(deliveryMethodId, country)) {
                            const showAsSuccess = Boolean(
                                !errors?.["date"] && touched?.["date"] && !!values["date"]
                            );
                            const iconProps = showAsSuccess ? getIconProps("date") : {};
                            return (
                                <DeliveryDatePicker
                                    {...getFieldStateProps("date")}
                                    isSuccess={showAsSuccess}
                                    {...iconProps}
                                    testId={`${prefix}-${DELIVERY_DATE}`}
                                    key={singleField}
                                    prefix={`${prefix}.`}
                                    header={headers.deliveryDate}
                                    setFieldValue={(field, value, shouldValidate) => {
                                        setFieldTouched(field, true);
                                        return setFieldValue(field, value, shouldValidate);
                                    }}
                                    deliveryDates={deliveryDates}
                                    initialValue={values.date}
                                    locale={locale}
                                />
                            );
                        }
                        break;
                    }
                    case Address.form.deliveryTimeSlot:
                        if (isDateSelectorEnabled(deliveryMethodId, country)) {
                            return (
                                <TimeslotSelector
                                    testId={`${prefix}-${DELIVERY_TIME}`}
                                    key={singleField}
                                    prefix={`${prefix}.`}
                                    header={headers.deliveryTime}
                                    setFieldValue={setFieldValue}
                                    dropdownStyle={styles.dropdownStyle}
                                    initialValue={values.timeslot}
                                    options={headers.timeslotOptions}
                                />
                            );
                        }
                        break;
                    case Address.form.salutation:
                        if (deliveryMethodId === ShippingMethodTypes.PARCEL_SHOP) {
                            return (
                                <SalutationField
                                    testId={`${prefix}-${SALUTATION_TEST_ID}`}
                                    key={singleField}
                                    prefix={`${prefix}.`}
                                    error={errors?.salutation}
                                    value={values.salutation}
                                    header={headers.salutation}
                                    title={headers.salutationPlaceholder}
                                    isSuccess={!errors?.salutation && touched?.salutation}
                                    isInvalid={!!errors?.salutation && touched?.salutation}
                                    setFieldValue={setFieldValue}
                                    country={country}
                                    locale={locale}
                                    style={styles.fieldWrapper}
                                    stateDropdownStyle={styles.dropdownStyle}
                                    isReadOnly={isFieldReadOnly(singleField)}
                                />
                            );
                        }
                        break;
                }
            })}
        </>
    );
};

export default AddressFormFields;
